import {type MutableRefObject, type PointerEvent as ReactPointerEvent, type ReactNode, useEffect, useRef, useState} from "react";

import {cn} from "@lib/utils";

interface StickyHorizontalScrollFrameProps {
    children: ReactNode;
    className?: string;
    viewportClassName?: string;
    scrollTargetRef?: MutableRefObject<HTMLElement | null>;
}

interface ScrollMetrics {
    visible: boolean;
    thumbWidth: number;
    thumbOffset: number;
}

function findScrollTarget(root: HTMLElement | null): HTMLElement | null {
    if (!root) return null;
    return root.querySelector<HTMLElement>("[data-slot='table-container']")
        ?? root.querySelector<HTMLElement>("[data-sticky-scroll-target='true']")
        ?? root.firstElementChild as HTMLElement | null;
}

export function StickyHorizontalScrollFrame({
    children,
    className,
    viewportClassName,
    scrollTargetRef: externalScrollTargetRef,
}: StickyHorizontalScrollFrameProps) {
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const trackRef = useRef<HTMLDivElement | null>(null);
    const dragStateRef = useRef<{
        startX: number;
        startScrollLeft: number;
        maxScrollLeft: number;
        maxThumbOffset: number;
    } | null>(null);
    const scrollTargetRef = useRef<HTMLElement | null>(null);
    const [metrics, setMetrics] = useState<ScrollMetrics>({
        visible: false,
        thumbWidth: 0,
        thumbOffset: 0,
    });

    useEffect(() => {
        const viewport = viewportRef.current;
        const track = trackRef.current;
        const scrollTarget = findScrollTarget(viewport);
        if (!viewport || !track || !scrollTarget) return;

        scrollTargetRef.current = scrollTarget;
        if (externalScrollTargetRef) {
            externalScrollTargetRef.current = scrollTarget;
        }

        const updateMetrics = () => {
            const trackWidth = track.clientWidth;
            const scrollWidth = scrollTarget.scrollWidth;
            const clientWidth = scrollTarget.clientWidth;
            const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0);

            if (trackWidth <= 0 || maxScrollLeft <= 0) {
                setMetrics({visible: false, thumbWidth: 0, thumbOffset: 0});
                return;
            }

            const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 56);
            const maxThumbOffset = Math.max(trackWidth - thumbWidth, 0);
            const thumbOffset = maxScrollLeft === 0
                ? 0
                : (scrollTarget.scrollLeft / maxScrollLeft) * maxThumbOffset;

            setMetrics({
                visible: true,
                thumbWidth,
                thumbOffset,
            });
        };

        const resizeObserver = typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(updateMetrics)
            : null;

        resizeObserver?.observe(track);
        resizeObserver?.observe(scrollTarget);
        if (scrollTarget.firstElementChild instanceof HTMLElement) {
            resizeObserver?.observe(scrollTarget.firstElementChild);
        }

        scrollTarget.addEventListener("scroll", updateMetrics, {passive: true});
        window.addEventListener("resize", updateMetrics);
        updateMetrics();

        return () => {
            resizeObserver?.disconnect();
            scrollTarget.removeEventListener("scroll", updateMetrics);
            window.removeEventListener("resize", updateMetrics);
            scrollTargetRef.current = null;
            if (externalScrollTargetRef) {
                externalScrollTargetRef.current = null;
            }
        };
    }, [children, externalScrollTargetRef]);

    useEffect(() => {
        const handlePointerMove = (event: PointerEvent) => {
            const dragState = dragStateRef.current;
            const scrollTarget = scrollTargetRef.current;
            if (!dragState || !scrollTarget) return;

            const deltaX = event.clientX - dragState.startX;
            const thumbRatio = dragState.maxThumbOffset > 0
                ? deltaX / dragState.maxThumbOffset
                : 0;
            scrollTarget.scrollLeft = dragState.startScrollLeft + (thumbRatio * dragState.maxScrollLeft);
        };

        const clearDrag = () => {
            dragStateRef.current = null;
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
        };

        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", clearDrag);
        window.addEventListener("pointercancel", clearDrag);

        return () => {
            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", clearDrag);
            window.removeEventListener("pointercancel", clearDrag);
        };
    }, []);

    const beginThumbDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
        const track = trackRef.current;
        const scrollTarget = scrollTargetRef.current;
        if (!track || !scrollTarget || !metrics.visible) return;

        const maxScrollLeft = Math.max(scrollTarget.scrollWidth - scrollTarget.clientWidth, 0);
        const maxThumbOffset = Math.max(track.clientWidth - metrics.thumbWidth, 0);
        dragStateRef.current = {
            startX: event.clientX,
            startScrollLeft: scrollTarget.scrollLeft,
            maxScrollLeft,
            maxThumbOffset,
        };
        document.body.style.userSelect = "none";
        document.body.style.cursor = "ew-resize";
    };

    const jumpToTrackPosition = (event: ReactPointerEvent<HTMLDivElement>) => {
        const track = trackRef.current;
        const scrollTarget = scrollTargetRef.current;
        if (!track || !scrollTarget || !metrics.visible) return;

        const rect = track.getBoundingClientRect();
        const clickOffset = event.clientX - rect.left;
        const centeredOffset = clickOffset - (metrics.thumbWidth / 2);
        const maxThumbOffset = Math.max(track.clientWidth - metrics.thumbWidth, 0);
        const clampedOffset = Math.max(0, Math.min(centeredOffset, maxThumbOffset));
        const maxScrollLeft = Math.max(scrollTarget.scrollWidth - scrollTarget.clientWidth, 0);
        const ratio = maxThumbOffset > 0 ? clampedOffset / maxThumbOffset : 0;

        scrollTarget.scrollLeft = ratio * maxScrollLeft;
    };

    return (
        <div className={cn("relative", className)} data-sticky-scroll-frame="true">
            <style>{`
                [data-sticky-scroll-frame="true"] [data-slot="table-container"] {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                [data-sticky-scroll-frame="true"] [data-slot="table-container"]::-webkit-scrollbar {
                    height: 0;
                }
            `}</style>
            <div ref={viewportRef} className={viewportClassName}>
                {children}
            </div>
            <div className={cn("sticky bottom-3 z-40 mt-2 transition-opacity", metrics.visible ? "opacity-100" : "pointer-events-none opacity-0")}>
                <div className="rounded-full border border-slate-200/80 bg-white/90 px-2 py-1 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-white/75">
                    <div
                        ref={trackRef}
                        data-testid="sticky-horizontal-scrollbar"
                        className="relative h-2 rounded-full bg-slate-200/90"
                        onPointerDown={jumpToTrackPosition}
                    >
                        <div
                            role="scrollbar"
                            aria-label="Horizontal table scroll"
                            aria-orientation="horizontal"
                            aria-valuemin={0}
                            aria-valuenow={Math.round(metrics.thumbOffset)}
                            aria-valuemax={Math.round(Math.max((trackRef.current?.clientWidth ?? 0) - metrics.thumbWidth, 0))}
                            className="absolute top-0 h-2 cursor-ew-resize rounded-full bg-slate-500/80 shadow-sm transition-colors hover:bg-slate-600"
                            style={{
                                width: `${metrics.thumbWidth}px`,
                                transform: `translateX(${metrics.thumbOffset}px)`,
                            }}
                            onPointerDown={(event) => {
                                event.stopPropagation();
                                beginThumbDrag(event);
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
